import { Tag } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { SelectFilter } from '@/containers/billing/register/billingFilters/filter/SelectFilter'
import { NewClientData } from '@/containers/billing/register/billingPortals/newRegisterWizard/NewRegisterWizard'
import {
  CountryPhoneSelector,
  CountrySelector,
} from '@/containers/billing/register/billingPortals/newRegisterWizard/steps/components'
import { Country } from '@/interfaces/billing'
import { cn } from '@/lib/utils'

import { useCustomerCategories } from '../hooks/useCustomerCategories'
import { useMunicipalitiesList } from '../hooks/useMunicipalitiesList'
import { useProvincesList } from '../hooks/useProvincesList'
import { ClientValidationErrors } from '../types'

interface NewClientFormProps {
  clientData: NewClientData
  onClientDataChange: (_updates: Partial<NewClientData>) => void
  onSwitchToSearch: () => void
  validationErrors: ClientValidationErrors
  onFieldChange: (_field: keyof ClientValidationErrors) => void
  onFieldFocus?: (_field: string) => void
  disabled?: boolean
  className?: string
  clientContainer?: boolean
}

// Error display component
const ErrorText = ({
  error,
  className,
}: {
  error?: string
  className?: string
}) => {
  if (!error) return null
  return <p className={cn('text-sm text-red-500', className)}>{error}</p>
}

export function NewClientForm({
  clientData,
  onClientDataChange,
  onSwitchToSearch,
  validationErrors,
  onFieldChange,
  onFieldFocus,
  disabled = false,
  className,
  clientContainer = false,
}: NewClientFormProps) {
  const {
    categoryOptions,
    categoriesToStringArray,
    stringArrayToCategories,
    isLoading: isLoadingCategories,
    hasError: hasCategoriesError,
    error: categoriesError,
  } = useCustomerCategories()

  const { provinceOptions, isLoadingProvinces, isErrorProvinces } =
    useProvincesList({
      countryId: clientData.country?.id ? parseInt(clientData.country.id) : 54,
    })
  const {
    municipalityOptions,
    isLoadingMunicipalities,
    isErrorMunicipalities,
  } = useMunicipalitiesList({
    provinceId: clientData.province?.id ? parseInt(clientData.province.id) : 0,
  })

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    onFieldChange(name as keyof ClientValidationErrors)
    onClientDataChange({ [name]: value })
  }

  const handleInputFocus = (field: string) => {
    onFieldFocus?.(field)
  }

  const handleSelectChange = (name: keyof NewClientData, value: string) => {
    onFieldChange(name as keyof ClientValidationErrors)

    if (name === 'province') {
      onClientDataChange({
        province: {
          id: value,
          name: provinceOptions.find((p) => p.value === value)?.label || '',
        },
        municipality: {
          id: '',
          name: '',
        }, // Reset municipality
      })
    } else if (name === 'municipality') {
      onClientDataChange({
        municipality: {
          id: value,
          name: municipalityOptions.find((m) => m.value === value)?.label || '',
        },
      })
    }
  }

  const handlePhoneChange = (phoneData: {
    number: string
    codeCountry: string
  }) => {
    onFieldChange('phone')
    onClientDataChange({ phone: phoneData })
  }

  const handleCountryChange = (country: Country) => {
    onFieldChange('country')
    onClientDataChange({
      country: {
        id: country.id.toString(),
        name: country.name,
      },
      province: {
        id: '',
        name: '',
      },
      municipality: {
        id: '',
        name: '',
      },
    })
  }

  const handleCategoryChange = (selectedCategories: string[]) => {
    onFieldChange('categories')
    const categoryIds = stringArrayToCategories(selectedCategories)
    onClientDataChange({ categories: categoryIds })
  }

  return (
    <div className={className}>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-medium">
          {clientContainer ? 'Generales' : 'Datos del Nuevo Cliente'}
        </h3>
        {!clientContainer && (
          <Button
            disabled={disabled}
            onClick={onSwitchToSearch}
            size="sm"
            variant="outline"
          >
            Buscar Existente
          </Button>
        )}
      </div>

      {/* Show loading/error states for dependencies */}
      {isLoadingProvinces ||
        (isLoadingMunicipalities && (
          <div className="mb-4 text-sm text-muted-foreground">
            Cargando datos de ubicación...
          </div>
        ))}

      <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2">
        {/* Basic Info */}
        <div className="space-y-1">
          <Label htmlFor="name">Nombre (*)</Label>
          <Input
            className={validationErrors.name ? 'border-red-500' : ''}
            disabled={disabled}
            id="name"
            name="name"
            onChange={handleInputChange}
            onFocus={() => handleInputFocus('name')}
            required
            value={clientData.name}
          />
          <ErrorText error={validationErrors.name} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="lastName">Apellidos (*)</Label>
          <Input
            className={validationErrors.lastName ? 'border-red-500' : ''}
            disabled={disabled}
            id="lastName"
            name="lastName"
            onChange={handleInputChange}
            onFocus={() => handleInputFocus('lastName')}
            required
            value={clientData.lastName || ''}
          />
          <ErrorText error={validationErrors.lastName} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="idNumber">No. de Identificación</Label>
          <Input
            disabled={disabled}
            id="idNumber"
            name="idNumber"
            onChange={handleInputChange}
            value={clientData.idNumber || ''}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="phone">Teléfono</Label>
          <CountryPhoneSelector
            className={
              validationErrors.phone ? 'rounded-md border-red-500' : ''
            }
            disabled={disabled}
            onChange={handlePhoneChange}
            value={clientData.phone}
          />
          <ErrorText error={validationErrors.phone} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="email">Correo electrónico</Label>
          <Input
            className={validationErrors.email ? 'border-red-500' : ''}
            disabled={disabled}
            id="email"
            name="email"
            onChange={handleInputChange}
            onFocus={() => handleInputFocus('email')}
            type="email"
            value={clientData.email || ''}
          />
          <ErrorText error={validationErrors.email} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="contractNumber">No. de Contrato</Label>
          <Input
            disabled={disabled}
            id="contractNumber"
            name="contractNumber"
            onChange={handleInputChange}
            value={clientData.contractNumber || ''}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="organization">Organización</Label>
          <Input
            disabled={disabled}
            id="organization"
            name="organization"
            onChange={handleInputChange}
            value={clientData.organization || ''}
          />
        </div>

        <div className="mt-1 flex flex-col gap-3">
          <Label htmlFor="categories">Categorías del Cliente</Label>
          <div onFocus={() => handleInputFocus('categories')}>
            <SelectFilter
              activeSelections={categoriesToStringArray(clientData.categories)}
              className="w-fit rounded-md border"
              icon={<Tag className="h-4 w-4" />}
              onSelectionsChange={handleCategoryChange}
              placeholder="Buscar categorías..."
              searchActive={false}
              selectionOptions={categoryOptions}
              title="Categorías"
            />
          </div>
          {isLoadingCategories && (
            <p className="text-sm text-muted-foreground">
              Cargando categorías...
            </p>
          )}
          {hasCategoriesError && (
            <p className="text-sm text-red-500">
              Error cargando categorías: {categoriesError?.message}
            </p>
          )}
        </div>

        <div className="space-y-1 md:col-span-2">
          <Label htmlFor="legalNotes">Notas Legales</Label>
          <Textarea
            disabled={disabled}
            id="legalNotes"
            name="legalNotes"
            onChange={handleInputChange}
            rows={2}
            value={clientData.legalNotes || ''}
          />
        </div>

        {/* Address Info */}
        <h4 className="text-md mt-3 border-t pt-4 font-medium md:col-span-2">
          Dirección
        </h4>

        <div className="space-y-1">
          <Label htmlFor="mainAddress">Calle Principal (*)</Label>
          <Input
            className={validationErrors.mainAddress ? 'border-red-500' : ''}
            disabled={disabled}
            id="mainAddress"
            name="mainAddress"
            onChange={handleInputChange}
            onFocus={() => handleInputFocus('mainAddress')}
            value={clientData.mainAddress || ''}
          />
          <ErrorText error={validationErrors.mainAddress} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="secondaryAddress">Calle Secundaria</Label>
          <Input
            disabled={disabled}
            id="secondaryAddress"
            name="secondaryAddress"
            onChange={handleInputChange}
            value={clientData.secondaryAddress || ''}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="locality">Localidad</Label>
          <Input
            disabled={disabled}
            id="locality"
            name="locality"
            onChange={handleInputChange}
            value={clientData.locality || ''}
          />
        </div>

        <div className="mt-[0.35rem] flex flex-col gap-2">
          <Label htmlFor="country">País (*)</Label>
          <CountrySelector
            className={cn(validationErrors.country ? 'border-red-500' : '')}
            disabled={disabled}
            onChange={handleCountryChange}
            placeholder="Seleccionar país"
            showFlag={true}
            showPrefix={false}
            value={clientData.country?.name || 'Cuba'}
            valueType="name"
          />
          <ErrorText error={validationErrors.country} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="province">Provincia (*)</Label>
          <Select
            disabled={
              disabled ||
              !clientData.country?.id ||
              isLoadingProvinces ||
              isErrorProvinces
            }
            onValueChange={(value) => handleSelectChange('province', value)}
            value={clientData.province?.id?.trim() ?? undefined}
          >
            <SelectTrigger
              className={validationErrors.province ? 'border-red-500' : ''}
              onFocus={() => handleInputFocus('province')}
            >
              <SelectValue
                placeholder={
                  isLoadingProvinces
                    ? 'Cargando...'
                    : isErrorProvinces
                      ? 'Error cargando provincias'
                      : 'Seleccionar provincia'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {provinceOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ErrorText error={validationErrors.province} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="municipality">Municipio (*)</Label>
          <Select
            disabled={
              disabled ||
              !clientData.province?.id?.trim() ||
              isLoadingMunicipalities ||
              isErrorMunicipalities
            }
            onValueChange={(value) => handleSelectChange('municipality', value)}
            value={clientData.municipality?.id?.trim() ?? undefined}
          >
            <SelectTrigger
              className={validationErrors.municipality ? 'border-red-500' : ''}
              onFocus={() => handleInputFocus('municipality')}
            >
              <SelectValue
                placeholder={
                  !clientData.province?.id?.trim()
                    ? 'Selecciona provincia primero'
                    : isLoadingMunicipalities
                      ? 'Cargando...'
                      : isErrorMunicipalities
                        ? 'Error cargando municipios'
                        : 'Seleccionar municipio'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {municipalityOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ErrorText error={validationErrors.municipality} />
        </div>
      </div>
    </div>
  )
}
